import { useEffect, useState } from 'react';
import DataGridLayout from '../../components/layout/DataGridLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Mail, Phone, Trash2, User, MessageSquare, MessageCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import VerificationToggle from '../../components/ui/VerificationToggle';
import { EnquiryRequest } from '../../types/enquiry';
import { app } from '../../firebase';
import { collection, doc, getDocs, getFirestore, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';


const ContactusRequests = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const db = getFirestore(app);
  const auth = getAuth(app);
  const [contactUs, setContactUs] = useState([]);



  useEffect(() => {
    // Inside your component  
    const fetchEnquires = async () => {

      try {
        const snapshot = await getDocs(collection(db, 'contactus'));
        const contactUsData: any = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setContactUs(contactUsData);
      } catch (error) {
        toast({
          title: "Error Fetching ContactUs",
          description: error.message,
          variant: "destructive",
        });
      }
    };

    fetchEnquires();
  }, [])

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    toast({
      title: "Refreshed",
      description: "Contact request data has been refreshed successfully.",
    });
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Your contact request data export has been initiated.",
    });
  };

  const handleToggleVerification = async (id: string) => {
    setContactUs(prevRequests =>
      prevRequests.map(request =>
        request.id === id
          ? { ...request, isVerified: !request.isVerified }
          : request
      )
    );
    let record: any = contactUs.filter(x => x.id == id)[0];
    record = {
      ...record,
      isVerified: !record.isVerified
    }
    try {
      const contactUsRef = doc(db, "contactus", id); // Get a reference to the course document  
      await setDoc(contactUsRef, record); // Update the course data in Firestore  
      toast({
        title: "Verification Status Updated",
        description: "The verification status has been updated successfully.",
      });

    } catch (error) {
      toast({
        title: "Verification Failed",
        description: "The verification updation has been failed."
      });
    }
  };

  const handleDelete = (id: string) => {
    setContactUs(prevRequests => prevRequests.filter(request => request.id !== id));

    toast({
      title: "Entry Deleted",
      description: "The contact request has been removed successfully.",
    });
  };

  const filteredRequests = contactUs?.filter(request =>
    request.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.emailId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.mobileNo.includes(searchQuery) ||
    (request.query && request.query.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (request.feedback && request.feedback.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DataGridLayout
      title="Contact Us Requests"
      subtitle="Manage user contact requests and queries"
      searchPlaceholder="Search by name, email, phone or query..."
      onSearch={handleSearch}
      onRefresh={handleRefresh}
      onExport={handleExport}
    >
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full Name</TableHead>
              <TableHead>Mobile No</TableHead>
              <TableHead>Any Query</TableHead>
              <TableHead>Any Feedback</TableHead>
              <TableHead className="text-center">Is Verified</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No contact requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-blue-500" />
                      {request.fullName}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-green-500" />
                      {request.mobileNo}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MessageSquare size={16} className="text-purple-500" />
                      {request.query || 'No query'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MessageCircle size={16} className="text-amber-500" />
                      {request.feedback || 'No feedback'}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <VerificationToggle
                      isVerified={request.isVerified}
                      onToggle={() => handleToggleVerification(request.id)}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(request.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </DataGridLayout>
  );
};

export default ContactusRequests;
